import { useEffect, useState } from 'react';

import type { ApiListingApplicationId } from 'api/types/ApiTypedId';
import { Box } from 'components/Box/Box';
import { InternshipDetailsList } from 'components/DetailsList/Internship/InternshipDetailsList';
import { PageToolbar } from 'components/PageToolbar/PageToolbar';
import { PrintHidden } from 'components/PrintHidden/PrintHidden';
import { useUser } from 'contexts/UserEnvironmentContext';
import { useSetDocumentTitle } from 'hooks/useSetDocumentTitle';
import type { ApiInternship } from 'modules/listing/api/internship/types/ApiInternship';
import { ListingViewAdminTools } from 'modules/listing/components/ListingView/AdminTools/ListingViewAdminTools';
import { ListingViewBody } from 'modules/listing/components/ListingView/Body/ListingViewBody';
import { ListingViewFooter } from 'modules/listing/components/ListingView/Footer/ListingViewFooter';
import { ListingViewHeader } from 'modules/listing/components/ListingView/Header/ListingViewHeader';
import { ListingViewHeaderSearchContext } from 'modules/listing/components/ListingView/HeaderSearchContext/ListingViewHeaderSearchContext';
import { ListingViewMain } from 'modules/listing/components/ListingView/Main/ListingViewMain';
import { ListingViewSection } from 'modules/listing/components/ListingView/Section/ListingViewSection';
import { ListingViewSidebar } from 'modules/listing/components/ListingView/Sidebar/ListingViewSidebar';
import { ListingViewSidebarContent } from 'modules/listing/components/ListingView/Sidebar/ListingViewSidebarContent';
import { ListingViewSidebarImage } from 'modules/listing/components/ListingView/Sidebar/ListingViewSidebarImage';
import { ListingViewSidebarLinks } from 'modules/listing/components/ListingView/Sidebar/ListingViewSidebarLinks';
import { ListingViewSidebarTitle } from 'modules/listing/components/ListingView/Sidebar/ListingViewSidebarTitle';
import { ListingViewSignupCta } from 'modules/listing/components/ListingView/SignupCta/ListingViewSignupCta';
import { ListingViewDescriptionSection } from 'modules/listing/components/ListingView/sections/Description/ListingViewDescriptionSection';
import { ListingViewApplySection } from 'modules/listing/components/ListingView/sections/ListingViewApplySection';
import { ListingViewBenefitsSection } from 'modules/listing/components/ListingView/sections/ListingViewBenefitsSection';
import { ListingViewLocationSection } from 'modules/listing/components/ListingView/sections/ListingViewLocationSection';
import { useSaveListingAndOrg } from 'modules/listing/hooks/useSaveListingAndOrg';
import { ViewInternshipActionButtons } from 'modules/listing/internships/components/ActionButtons/ViewInternshipActionButtons';
import { SearchContextHeader } from 'modules/search/components/Context/SearchContextHeader';
import { useSearchContextHeader } from 'modules/search/containers/SearchContextHeader/useSearchContextHeader';
import { trackClicked } from 'utils/analytics/track/trackClicked';
import { trackListing } from 'utils/analytics/trackListing';
import { isUserOrgOwner } from 'utils/listings/getOrgOwner';
import { isExternalURL } from 'utils/url/isExternalURL';
import { showModal } from 'zustand-stores/modalStore';

type Props = {
  internship: ApiInternship;
  listingApplicationId: ApiListingApplicationId | null;
  initiallySaved: boolean;
  orgInitiallySaved: boolean;
};

export function ViewInternshipPage({
  internship,
  listingApplicationId,
  initiallySaved,
  orgInitiallySaved,
}: Props) {
  const {
    address,
    applyText,
    applyTextTruncated,
    applyUrl,
    applyEmail,
    id,
    hasAts,
    benefits,
    benefitsTruncated,
    startDate,
    endDate,
    functions,
    requestedAttachments,
    requestedAttachmentsHash,
    firstPublished,
    isPostedAnonymously,
    name,
    type,
    description,
    descriptionTruncated,
    remoteOptions,
    url,
    wage,
    org,
    orgNameOverride,
    orgLogoOverride,
    payment,
    hoursPerWeek,
    academicCredits,
    applicationDeadline,
  } = internship;

  const [applied, setApplied] = useState(Boolean(listingApplicationId));

  useSetDocumentTitle(name);

  const { user } = useUser();
  const isOrgOwner = isUserOrgOwner(user, org?.id);

  const { searchContext } = useSearchContextHeader({ currentObjectId: id });

  useEffect(() => {
    trackListing({
      status: 'published',
      hasAts,
      listingType: type,
      listingId: id,
      listingName: name,
      orgId: org?.id,
      orgName: org?.name,
      orgType: org?.orgType,
    });
  }, [hasAts, id, name, org?.id, org?.name, org?.orgType, type]);

  const orgLogo = orgLogoOverride ?? org?.logo ?? null;
  const orgName = orgNameOverride ?? org?.name ?? null;
  const orgUrl = org?.url[CURRENT_LOCALE()] ?? null;

  const {
    save,
    saved,
    unsave,
    orgSaved,
    saveOrg,
    unsaveOrg,
    shouldShowSaveOrgTooltip,
    dismissSaveOrgTooltip,
  } = useSaveListingAndOrg({
    listing: internship,
    orgInitiallySaved,
    initiallySaved,
    searchSavedListingsStore: null,
  });

  return (
    <>
      {isOrgOwner && org && (
        <PageToolbar
          centered
          message={getText('Your organization posted this internship')}
          tools={
            <ListingViewAdminTools
              listingId={id}
              listingType="INTERNSHIP"
              orgId={org.id}
              hasAts={hasAts}
              canPost
            />
          }
        />
      )}

      <ListingViewBody>
        <ListingViewSidebar maxWidth={288}>
          <ListingViewSidebarImage
            orgLogo={orgLogo}
            orgName={orgName}
            orgURLForCurrentLocale={orgUrl}
          />
          <ListingViewSidebarTitle
            address={address}
            listingName={name}
            listingType="INTERNSHIP"
            orgName={orgName}
            orgUrl={orgUrl}
            remoteOptions={remoteOptions}
          />
          <ListingViewSidebarContent
            firstPublished={firstPublished}
            listingType={type}
            hasAts={hasAts}
            applied={applied}
            isExpired={false}
            isPreview={false}
          />
          <ListingViewSidebarLinks
            searchContext={searchContext}
            orgUrl={orgUrl}
          />
        </ListingViewSidebar>

        <ListingViewMain>
          {searchContext && (
            <ListingViewHeaderSearchContext searchContext={searchContext} />
          )}

          <ListingViewHeader
            address={address}
            isPostedAnonymously={isPostedAnonymously}
            listingId={id}
            listingName={name}
            listingType={type}
            listingFirstPublished={firstPublished}
            orgName={orgName}
            orgType={org?.orgType ?? null}
            orgUrl={orgUrl}
            remoteOptions={remoteOptions}
            isExpired={false}
            actionButtons={
              <ViewInternshipActionButtons
                internship={internship}
                applied={applied}
                internshipSaved={saved}
                onSaveInternship={save}
                onUnsaveInternship={unsave}
                orgSaved={orgSaved}
                onSaveOrg={saveOrg}
                onUnsaveOrg={unsaveOrg}
                shouldShowSaveOrgTooltip={shouldShowSaveOrgTooltip}
                onDismissSaveOrgTooltip={dismissSaveOrgTooltip}
              />
            }
          />

          <ListingViewSection title={getText('Details')} noBorderTop>
            <InternshipDetailsList
              academicCredits={academicCredits}
              startDate={startDate}
              endDate={endDate}
              applicationDeadline={applicationDeadline}
              payment={payment}
              wage={wage}
              hoursPerWeek={hoursPerWeek}
            />
          </ListingViewSection>

          <ListingViewDescriptionSection
            listingName={name}
            image={null}
            description={description}
            descriptionTruncated={descriptionTruncated}
            handleShowEmailModal={(link) => {
              if (!user && isExternalURL(link.href)) {
                showModal('EMAIL_ALERT', {
                  listingFunctions: functions,
                  orgId: org?.id,
                  listingName: name,
                  listingType: 'INTERNSHIP',
                });
              }
            }}
            showMoreLinkTitle={getText('Read more about this internship')}
            onToggleDetails={(isOpen) => {
              if (!isOpen) return;

              trackClicked('read more about this listing', {
                listing_type: internship.type,
                locale: internship.locale,
              });
            }}
          />

          <ListingViewBenefitsSection
            benefits={benefits}
            benefitsTruncated={benefitsTruncated}
          />

          <ListingViewLocationSection
            listingType="INTERNSHIP"
            address={address}
            remoteOptions={remoteOptions}
          />

          <ListingViewApplySection
            applyEmail={applyEmail}
            applyText={applyText}
            applyTextTruncated={applyTextTruncated}
            applyUrl={applyUrl}
            applied={applied}
            onApplied={() => setApplied(true)}
            onMarkAsApplied={() => Promise.resolve()}
            functions={functions}
            hasAts={hasAts}
            listingName={name}
            listingType="INTERNSHIP"
            listingId={id}
            orgType={org?.orgType}
            orgId={org?.id}
            requestedAttachments={requestedAttachments}
            requestedAttachmentsHash={requestedAttachmentsHash}
            synonym={null}
          />

          {searchContext && (
            <PrintHidden>
              <Box mt={70} mb={48}>
                <SearchContextHeader searchContext={searchContext} noDivider />
              </Box>
            </PrintHidden>
          )}
        </ListingViewMain>
      </ListingViewBody>

      {!user && (
        <PrintHidden>
          <ListingViewFooter>
            <ListingViewSignupCta
              listingUrl={url[CURRENT_LOCALE()]}
              type={type}
            />
          </ListingViewFooter>
        </PrintHidden>
      )}
    </>
  );
}
